import React, { useState } from "react";
import { Info, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@pgs-core/ui/components/card";
import { Badge } from "@pgs-core/ui/components/badge";
import { Button } from "@pgs-core/ui/components/button";
import { Alert, AlertTitle, AlertDescription } from "@pgs-core/ui/components/alert";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@pgs-core/ui/components/table";
import type { BillingForecast } from "@pgs-core/types";
import { formatPence } from "@pgs-core/portal-lib/format";
import { ROLLING_ESTIMATE_BANNER } from "@pgs-core/portal-lib/billing";

const COLLAPSED_MONTHS = 12;

/**
 * Read-only payment schedule for the client: fixed-term months show locked
 * amounts; rolling months are estimates and carry the trend icon + banner.
 */
export function BillingForecastCard({ forecast }: { forecast: BillingForecast }) {
  const [showAll, setShowAll] = useState(false);
  const months = showAll ? forecast.months : forecast.months.slice(0, COLLAPSED_MONTHS);
  const hasRolling = forecast.months.some((m) => m.estimate);

  return (
    <Card className="mt-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Upcoming payments</CardTitle>
        <CardDescription>
          Your projected {forecast.billing_cadence === "annual" ? "annual" : "monthly"} payments
          {forecast.term ? ` — your fixed term runs until ${forecast.term.end}` : ""}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {hasRolling && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>About these figures</AlertTitle>
            <AlertDescription>{ROLLING_ESTIMATE_BANNER}</AlertDescription>
          </Alert>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Pricing</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {months.map((month) => (
              <TableRow key={month.charge_date}>
                <TableCell className="font-medium">{month.charge_date}</TableCell>
                <TableCell>
                  <Badge variant={month.phase === "fixed" ? "success" : "secondary"}>
                    {month.phase === "fixed" ? "Fixed term" : "Standard"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-medium">
                  <span className="inline-flex items-center gap-1">
                    {formatPence(month.total_pence)}
                    {month.estimate && (
                      <TrendingUp
                        className="h-3.5 w-3.5 text-muted-foreground"
                        aria-label="Estimate — follows future standard pricing"
                      />
                    )}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {forecast.months.length > COLLAPSED_MONTHS && (
          <Button size="sm" variant="outline" onClick={() => setShowAll((v) => !v)}>
            {showAll ? "Show first year only" : `Show all ${forecast.months.length} payments`}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
